import React, { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import API from 'src/api/config';

function stripMarkdown(text: string): string {
  return text
    .replace(/!\[(.*?)\]\(.*?\)/g, '$1')        // image alt text
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')         // links → just the link text
    .replace(/[*_`~]/g, '')                     // bold/italic/code/strike markers
    .replace(/^\s*[-•]\s+/gm, '')               // bullet markers at line starts
    .replace(/\n{2,}/g, '. ')                   // paragraph breaks become a pause
    .replace(/\n/g, ' ')                        // soft breaks become a space
    .replace(/\s{2,}/g, ' ')                    // collapse runs of whitespace
    .trim();
}

interface ReadAloudButtonProps {
  text: string;
  /** OpenAI TTS voice. Default 'nova' (warm female, good for elementary). */
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  /** Tooltip / aria label override. */
  label?: string;
}

/**
 * Reads the provided text aloud using server-side OpenAI TTS.
 * Backed by /ai/tts, which caches MP3s by content hash so repeated plays
 * don't re-bill OpenAI.
 */
export default function ReadAloudButton({
  text,
  voice = 'nova',
  label,
}: ReadAloudButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const handleClick = async () => {
    if (isPlaying) {
      stop();
      return;
    }

    const cleanText = stripMarkdown(text);
    if (!cleanText) return;

    setIsLoading(true);
    try {
      const response = await API.post(
        '/ai/tts',
        { text: cleanText, voice },
        { responseType: 'blob' },
      );

      // Replace any prior blob URL.
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      const url = URL.createObjectURL(response.data);
      objectUrlRef.current = url;

      const audio = new Audio(url);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      audioRef.current = audio;

      setIsLoading(false);
      setIsPlaying(true);
      await audio.play();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Read-aloud failed', err);
      setIsLoading(false);
      setIsPlaying(false);
    }
  };

  const tooltipLabel =
    label ?? (isLoading ? 'Loading…' : isPlaying ? 'Stop' : 'Read aloud');

  return (
    <Tooltip title={tooltipLabel}>
      <span>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label={tooltipLabel}
          disabled={isLoading}
          sx={{
            color: isPlaying ? '#C8102E' : '#5C4FB8',
            padding: '4px',
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} thickness={5} sx={{ color: '#5C4FB8' }} />
          ) : isPlaying ? (
            <StopIcon fontSize="small" />
          ) : (
            <VolumeUpIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
